import { CSSProperties } from "react";

import HairlineStatGrid from "@/components/HairlineStatGrid";
import podFounder from "@/assets/pod-founder.webp";
import podEngineering from "@/assets/pod-engineering.webp";
import podGrowth from "@/assets/pod-growth.webp";
import podSales from "@/assets/pod-sales.webp";
import podOps from "@/assets/pod-ops.webp";
import podCreator from "@/assets/pod-creator.webp";
import marqueeLion1 from "@/assets/marquee-lion-1.webp";
import marqueeLion2 from "@/assets/marquee-lion-2.webp";
import marqueeLion3 from "@/assets/marquee-lion-3.webp";
import marqueeLion4 from "@/assets/marquee-lion-4.webp";
import marqueeAgentPaw from "@/assets/marquee-agent-paw.jpg";
import marqueeAgentToken from "@/assets/marquee-agent-token.jpg";

/**
 * StatRowSection. "By the numbers" stats block.
 *
 * Layout (top → bottom):
 *  1. Centered eyebrow "By the numbers" in --accent + supporting subhead
 *  2. Marquee strip of agent / pod portraits, scrolling LTR slowly
 *  3. 4-column hairline stat grid with REAL numbers
 *  4. Marquee strip identical to #2 but scrolling RTL
 *
 * The marquee tiles reuse the agent imagery seen elsewhere on the homepage
 * (terminal / pod cards) so the by-the-numbers block visually echoes the
 * roster instead of using abstract palette swatches.
 */

// Real homepage numbers, grounded in copy already on the site.
// - 30+ specialist agents shipped across 6 pods (see AgentsRoster).
// - 5 channels live or imminent: Slack, Telegram, WhatsApp, Discord, Teams.
// - 100+ MCP integrations available out of the box.
// - 24/7 autonomous loop, agents keep working while you sleep.
const STATS: { value: string; label: string }[] = [
  { value: "40+", label: "Agents" },
  { value: "5", label: "Channels" },
  { value: "3k+", label: "Integrations" },
  { value: "24/7", label: "Hours autonomous" },
];

// Roster portraits, pod images and operator avatars used elsewhere on
// the site. Repeated to fill the marquee with continuous variety.
const TILE_IMAGES: { src: string; alt: string }[] = [
  { src: podFounder, alt: "Founder pod" },
  { src: marqueeLion1, alt: "Mane lion bust" },
  { src: podEngineering, alt: "Engineering pod" },
  { src: marqueeAgentToken, alt: "Agent token" },
  { src: podGrowth, alt: "Growth pod" },
  { src: marqueeLion2, alt: "Mane lion eye" },
  { src: podSales, alt: "Sales pod" },
  { src: marqueeAgentPaw, alt: "Mane paw glyph" },
  { src: podOps, alt: "Ops pod" },
  { src: marqueeLion3, alt: "Lion profile" },
  { src: podCreator, alt: "Creator pod" },
  { src: marqueeLion4, alt: "Lion front" },
];

const Tile = ({ img }: { img: { src: string; alt: string } }) => (
  <div
    className="shrink-0 overflow-hidden"
    style={{
      width: 32,
      height: 32,
      borderRadius: 3,
      border: "1px solid hsl(var(--border) / 0.55)",
      background: "hsl(var(--background))",
    }}
  >
    <img
      src={img.src}
      alt={img.alt}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover"
      style={{ filter: "grayscale(0.35) contrast(0.95)", opacity: 0.92 }}
    />
  </div>
);

const Marquee = ({ direction }: { direction: "ltr" | "rtl" }) => {
  const animation: CSSProperties = {
    animation: `${direction === "ltr" ? "marquee-ltr" : "marquee-rtl"} 60s linear infinite`,
  };
  // Repeat the image set enough times to fill the strip without gaps,
  // then duplicate the whole sequence so the looped translateX(-50%)
  // animation cycles seamlessly.
  const REPEAT = 4;
  const sequence = Array.from({ length: REPEAT }).flatMap(() => TILE_IMAGES);
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
      }}
    >
      <div className="flex gap-2 w-max" style={animation}>
        {[...sequence, ...sequence].map((img, i) => (
          <Tile key={i} img={img} />
        ))}
      </div>
    </div>
  );
};

const StatRowSection = () => (
  <section className="pt-12 pb-20 md:pt-16 md:pb-28">
    <div className="max-w-content mx-auto px-5 sm:px-8">
      {/* Inline keyframes, scoped, no Tailwind config change needed */}
      <style>{`
        @keyframes marquee-ltr {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        @keyframes marquee-rtl {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div>
        {/* Heading */}
        <div className="text-center mb-12">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono mb-4">
            By the numbers
          </p>
          <h2 className="h-display text-foreground max-w-[20ch] mx-auto">
            Real work, <em>measured.</em>
          </h2>
          <p className="mt-5 text-[15px] sm:text-[16px] text-muted-foreground leading-[1.55] max-w-[58ch] mx-auto">
            Mane is built for teams that need real autonomous work, measured, audited, and always on.
          </p>
        </div>

        {/* 4-column stat row */}
        <div>
          <HairlineStatGrid cells={STATS} />
        </div>
      </div>
    </div>
  </section>
);

export default StatRowSection;
